import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, input, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { Button } from '@shui/angular';
import { ShDatepickerAccessorComponent } from '../../../ui/sh-datepicker-accessor/sh-datepicker-accessor.component';
import { ShUserPickerComponent } from '../../../ui/sh-user-picker/sh-user-picker.component';
import { OutOfOfficeEntryEditorValue } from '../../models/out-of-office.model';
import { Roles } from '@shs-digi-care-monorepo/interfaces';

export type { OutOfOfficeEntryEditorValue };

/**
 * Value equality for the dateRange input. The parent re-binds a fresh `[]` / `[start, end]` array
 * literal on every change-detection pass; comparing by content (not reference) lets the derived
 * signal below suppress those no-op changes so the form sync doesn't wipe in-progress input.
 */
function dateRangeEqual(a: Date[], b: Date[]): boolean {
    return a.length === b.length && a.every((date, index) => date.getTime() === b[index].getTime());
}

@Component({
    selector: 'app-out-of-office-entry-editor',
    standalone: true,
    templateUrl: './out-of-office-entry-editor.component.html',
    imports: [ReactiveFormsModule, TranslatePipe, Button, ShDatepickerAccessorComponent, ShUserPickerComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class OutOfOfficeEntryEditorComponent {
    public readonly dateRange = input<Date[]>([]);
    public readonly user = input<string>('');
    public readonly mode = input<'create' | 'edit'>('edit');
    public readonly isSaving = input<boolean>(false);

    public readonly updateClicked = output<OutOfOfficeEntryEditorValue>();
    public readonly deleteClicked = output<void>();
    public readonly cancelClicked = output<void>();

    protected readonly roles = Roles;

    protected readonly form = new FormGroup({
        dateRange: new FormControl<Date[]>([]),
        user: new FormControl<string>('')
    });

    // Signal inputs don't accept an equality fn in this Angular build, so derive one that does: this
    // only reports a change when the dateRange VALUE differs, absorbing the parent's fresh-literal
    // re-binds. (user is a string — default Object.is equality already handles it.)
    private readonly syncedDateRange = computed(() => this.dateRange(), { equal: dateRangeEqual });

    constructor() {
        // Sync inputs -> form on genuine value changes only. Because syncedDateRange ignores no-op
        // re-binds, an incidental re-render can't re-patch and wipe in-progress user input.
        effect(() => {
            this.form.patchValue({ dateRange: this.syncedDateRange(), user: this.user() }, { emitEvent: false });
        });
    }

    protected onUpdate(): void {
        this.updateClicked.emit({
            dateRange: this.form.controls.dateRange.value ?? [],
            user: this.form.controls.user.value ?? ''
        });
    }

    protected onDelete(): void {
        this.deleteClicked.emit();
    }

    protected onCancel(): void {
        this.cancelClicked.emit();
    }
}
